import { useCallback, useEffect, useRef, useState } from "react";

import { Box, Button, Group, Modal, Tabs, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconEdit } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { Container, LayoutStandard } from "components";
import { IRootState } from "config/redux";
import { AppConstants, AuthorityConstants, ServiceConstants } from "constant";
import { useServiceSuccess } from "hooks";
import { AbsenceService, HolidayService, UserDataService, UserService } from "services";
import { displayClasses } from "styles";
import { HolidayResponse, UserDataOptionsResponse, UserDataRequest, UserDataResponse, UserResponse } from "types";
import { AppUtils, DateUtils, StorageUtils, UserUtils } from "utils";

import AbsenceRequestModal from "./AbsenceRequestModal";
import AbsencesTab, { AbsenceResponse } from "./AbsencesTab";
import DocumentationTab from "./DocumentationTab";
import KPITab from "./kpi/KPITab";
import ProfileHeader from "./ProfileHeader";
import ProfileUserDataModal from "./ProfileUserDataModal";

const { STORAGE_USER } = AppConstants;
const {
  REQUEST_ABSENCE_LIST_ID,
  REQUEST_USERS_LIST_ID,
  REQUEST_ABSENCE_APPROVE_ID,
  REQUEST_ABSENCE_REJECT_ID,
  REQUEST_HOLIDAY_LIST_ID,
  REQUEST_PENDING,
} = ServiceConstants;

const CENTRAL_PHONE = "+41 62 842 6000";

function formatDisplayValue(value?: string | null) {
  return value?.trim() ? value : "-";
}

function humanizeEnumValue(value: string) {
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const ProfilePage = () => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [absences, setAbsences] = useState<AbsenceResponse[]>([]);
  const [holidays, setHolidays] = useState<HolidayResponse[]>([]);
  const [selectedAbsenceId, setSelectedAbsenceId] = useState<number | null>(null);
  const [loggedUser] = useState<UserResponse | undefined>(StorageUtils.getObject(STORAGE_USER));
  const [fetchedUser, setFetchedUser] = useState<UserResponse | undefined>(undefined);
  const [userData, setUserData] = useState<UserDataResponse | null>(null);
  const [userDataOptions, setUserDataOptions] = useState<UserDataOptionsResponse | undefined>(
    undefined,
  );
  const [isUserDataLoading, setIsUserDataLoading] = useState(false);
  const [isUserDataSaving, setIsUserDataSaving] = useState(false);
  const [isUserDataOptionsLoading, setIsUserDataOptionsLoading] = useState(false);
  const [isEditUserDataOpened, setIsEditUserDataOpened] = useState(false);

  // Check if user can view other profiles (only admin with ROLE_USR_AGT)
  const canViewOtherProfiles = UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_AGT);
  
  // Permission checks for absence features
  const canRequestAbsence = UserUtils.hasAuthority(AuthorityConstants.ROLE_ABS);
  const canApproveAbsence = UserUtils.hasAuthority(AuthorityConstants.ROLE_ABS_CLOSE);

  // Get user from navigation state (when admin views agent profile)
  const stateUser = (location.state as { user?: UserResponse })?.user;

  // Only allow viewing other profile if user has permission
  const isViewingOtherProfile = !!id && canViewOtherProfiles;

  // Permission check for documentation tab
  const canViewDocumentation = isViewingOtherProfile
    ? UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_ATT)
    : UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_ATT_MY);

  // Determine which user data to display
  const user = isViewingOtherProfile ? stateUser || fetchedUser : loggedUser;
  const targetUserId = isViewingOtherProfile && id ? Number(id) : loggedUser?.id;
  const canEditPersonalData = Boolean(targetUserId) && (!isViewingOtherProfile || canViewOtherProfiles);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // Fetch user data if viewing other profile and no state available
  const usersServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_LIST_ID],
  );

  useEffect(() => {
    if (isViewingOtherProfile && !stateUser && id) {
      // Fetch users list to find the user by ID
      UserService.usersAll({
        page: 0,
        search: "",
        pageSize: 1000,
        profileId: 2,
        status: "ACTIVE",
        type: "EMPLOYEE",
      });
    }
  }, [isViewingOtherProfile, stateUser, id]);

  useEffect(() => {
    if (
      usersServiceObject?.status === "SUCCESS" &&
      usersServiceObject?.data?.content &&
      id &&
      !stateUser
    ) {
      const foundUser = usersServiceObject.data.content.find(
        (u: UserResponse) => u.id === Number(id),
      );
      if (foundUser) {
        setFetchedUser(foundUser);
      }
    }
  }, [usersServiceObject, id, stateUser]);

  const loadUserDataOptions = useCallback(async () => {
    if (userDataOptions || isUserDataOptionsLoading) {
      return;
    }

    setIsUserDataOptionsLoading(true);

    try {
      const data = await UserDataService.getUserDataOptions();
      setUserDataOptions(data);
    } catch {
      notifications.show({
        color: "red",
        title: tRef.current("profilePage.notifications.optionsError.title"),
        message: tRef.current("profilePage.notifications.optionsError.message"),
      });
    } finally {
      setIsUserDataOptionsLoading(false);
    }
  }, [isUserDataOptionsLoading, userDataOptions]);

  useEffect(() => {
    let isActive = true;

    setUserData(null);
    setIsEditUserDataOpened(false);

    if (!targetUserId) {
      setIsUserDataLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsUserDataLoading(true);

    const run = async () => {
      try {
        const data = isViewingOtherProfile
          ? await UserDataService.getUserData(targetUserId)
          : await UserDataService.getMyUserData();

        if (isActive) {
          setUserData(data);
        }
      } catch (err: any) {
        if (!isActive) {
          return;
        }

        if (UserDataService.isUserDataNotFoundError(err)) {
          setUserData(null);
          return;
        }

        notifications.show({
          color: "red",
          title: tRef.current("profilePage.notifications.loadError.title"),
          message: tRef.current("profilePage.notifications.loadError.message"),
        });
      } finally {
        if (isActive) {
          setIsUserDataLoading(false);
        }
      }
    };

    void run();

    return () => {
      isActive = false;
    };
  }, [isViewingOtherProfile, targetUserId]);

  const handleEditUserDataOpen = useCallback(() => {
    setIsEditUserDataOpened(true);
    void loadUserDataOptions();
  }, [loadUserDataOptions]);

  const handleEditUserDataClose = useCallback(() => {
    setIsEditUserDataOpened(false);
  }, []);

  const handleUserDataSubmit = useCallback(
    async (data: UserDataRequest) => {
      if (!targetUserId) {
        return;
      }

      setIsUserDataSaving(true);

      try {
        const savedData = isViewingOtherProfile
          ? userData?.id
            ? await UserDataService.updateUserData(targetUserId, data)
            : await UserDataService.createUserData(targetUserId, data)
          : userData?.id
            ? await UserDataService.updateMyUserData(data)
            : await UserDataService.createMyUserData(data);

        setUserData(savedData);
        setIsEditUserDataOpened(false);
        notifications.show({
          color: "green",
          title: tRef.current("profilePage.notifications.saveSuccess.title"),
          message: tRef.current("profilePage.notifications.saveSuccess.message"),
        });
      } catch {
        notifications.show({
          color: "red",
          title: tRef.current("profilePage.notifications.saveError.title"),
          message: tRef.current("profilePage.notifications.saveError.message"),
        });
      } finally {
        setIsUserDataSaving(false);
      }
    },
    [isViewingOtherProfile, targetUserId, userData?.id],
  );

  const handleNewRequest = useCallback(() => {
    setSelectedAbsenceId(null);
    openModal();
  }, [openModal]);

  const handleAbsenceClick = useCallback(
    (id: number) => {
      setSelectedAbsenceId(id);
      openModal();
    },
    [openModal],
  );

  const handleModalClose = useCallback(() => {
    setSelectedAbsenceId(null);
    closeModal();
  }, [closeModal]);

  const [statusModalOpened, setStatusModalOpened] = useState(false);
  const [selectedAbsenceForStatus, setSelectedAbsenceForStatus] = useState<AbsenceResponse | null>(
    null,
  );

  const handleStatusEditClick = useCallback((absence: AbsenceResponse) => {
    setSelectedAbsenceForStatus(absence);
    setStatusModalOpened(true);
  }, []);

  const handleStatusModalClose = useCallback(() => {
    setStatusModalOpened(false);
    setSelectedAbsenceForStatus(null);
  }, []);

  const handleApprove = useCallback(() => {
    if (selectedAbsenceForStatus) {
      AbsenceService.absenceApprove(selectedAbsenceForStatus.id);
    }
  }, [selectedAbsenceForStatus]);

  const handleReject = useCallback(() => {
    if (selectedAbsenceForStatus) {
      AbsenceService.absenceReject(selectedAbsenceForStatus.id);
    }
  }, [selectedAbsenceForStatus]);

  const getContractTypeLabel = useCallback(
    (value?: string | null) => {
      if (!value) {
        return "-";
      }

      return t(`profilePage.contractTypeOptions.${value}`, {
        defaultValue: humanizeEnumValue(value),
      });
    },
    [t],
  );

  const getLanguageLabel = useCallback(
    (value: string) =>
      t(`user.language.options.${value}`, {
        defaultValue: value,
      }),
    [t],
  );

  const personalName = userData?.realFirstName || userData?.realLastName
    ? `${userData?.realFirstName ?? ""} ${userData?.realLastName ?? ""}`.trim()
    : user?.fullName || "-";

  const profileData = {
    workName: user?.fullName || "-",
    workRole: user?.profile?.name || "-",
    workId: user?.id ? String(user.id) : "-",
    workEmail: formatDisplayValue(user?.email),
    workPhone:
      user?.countryCode && user?.phoneNumber ? `${user.countryCode} ${user.phoneNumber}` : "-",
    centralPhone: CENTRAL_PHONE,
    profileImage: user?.profileImage || undefined,
    personalName,
    privatePhone: formatDisplayValue(userData?.privatePhone),
    privateEmail: formatDisplayValue(userData?.privateEmail),
    startDate: userData?.workStartDate ? DateUtils.formatDateDisplay(userData.workStartDate) : "-",
    birthDate: userData?.birthDate ? DateUtils.formatDateDisplay(userData.birthDate) : "-",
    location: formatDisplayValue(userData?.location),
    contractType: getContractTypeLabel(userData?.contractType),
    contactPerson: formatDisplayValue(userData?.contactPerson),
    languages: userData?.languages?.length ? userData.languages.map(getLanguageLabel) : [],
  };

  const serviceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_ABSENCE_LIST_ID],
  );
  const isLoading = serviceObject?.status === REQUEST_PENDING;

  const fetchAbsences = useCallback(() => {
    if (isViewingOtherProfile && id) {
      AbsenceService.absenceList({ agentId: Number(id), pageSize: 100 });
    } else {
      AbsenceService.absenceList({ myAbsence: true, pageSize: 100 });
    }
  }, [isViewingOtherProfile, id]);

  // Status action success handler
  const handleStatusSuccess = useCallback(() => {
    handleStatusModalClose();
    fetchAbsences();
  }, [handleStatusModalClose, fetchAbsences]);

  // Track approve/reject success using custom hook
  const { isLoading: isApproveLoading } = useServiceSuccess(
    selectedAbsenceForStatus ? REQUEST_ABSENCE_APPROVE_ID + selectedAbsenceForStatus.id : undefined,
    { onSuccess: handleStatusSuccess },
  );
  const { isLoading: isRejectLoading } = useServiceSuccess(
    selectedAbsenceForStatus ? REQUEST_ABSENCE_REJECT_ID + selectedAbsenceForStatus.id : undefined,
    { onSuccess: handleStatusSuccess },
  );

  const isStatusActionLoading = isApproveLoading || isRejectLoading;

  useEffect(() => {
    if (!canRequestAbsence) {
      setAbsences([]);
      return;
    }

    setAbsences([]); // Reset absences when route changes
    fetchAbsences();
  }, [canRequestAbsence, fetchAbsences]);

  useEffect(() => {
    if (serviceObject?.status === "SUCCESS") {
      setAbsences(serviceObject?.data?.content || []);
    }
  }, [serviceObject]);

  // Fetch holidays
  const holidayServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_HOLIDAY_LIST_ID],
  );

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    void HolidayService.holidayList({
      dateFrom: `${currentYear}-01-01`,
      dateTo: `${currentYear}-12-31`,
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (holidayServiceObject?.status === "SUCCESS" && Array.isArray(holidayServiceObject.data)) {
      setHolidays(holidayServiceObject.data);
    }
  }, [holidayServiceObject]);

  return (
    <LayoutStandard title={AppUtils.composeTabTitle(t("profile"))}>
      <Container>
        <ProfileHeader
          data={profileData}
          editAction={
            canEditPersonalData ? (
              <Button
                variant="outline"
                color="gray"
                onClick={handleEditUserDataOpen}
                loading={isUserDataSaving || isUserDataLoading}
                leftSection={<IconEdit size={14} />}
              >
                {t("action.edit")}
              </Button>
            ) : undefined
          }
        />

        {/* Tabovi */}
        <Tabs defaultValue={canRequestAbsence ? "absences" : "kpi"} mt={36} variant="unstyled">
          <Tabs.List className={displayClasses.tabListWrap}>
            {canRequestAbsence && (
              <Tabs.Tab value="absences" className={displayClasses.tabList}>
                {t("profilePage.tabs.absences")}
              </Tabs.Tab>
            )}
            <Tabs.Tab value="kpi" className={displayClasses.tabList}>
              KPI
            </Tabs.Tab>
            {canViewDocumentation && (
              <Tabs.Tab value="documentation" className={displayClasses.tabList}>
                {t("profilePage.tabs.documentation")}
              </Tabs.Tab>
            )}
          </Tabs.List>

          {canRequestAbsence && (
            <Tabs.Panel value="absences" className={displayClasses.tabContent}>
              <AbsencesTab
                absences={absences}
                holidays={holidays}
                onRequestClick={handleNewRequest}
                onAbsenceClick={handleAbsenceClick}
                onStatusEditClick={canApproveAbsence ? handleStatusEditClick : undefined}
                loading={isLoading}
                readOnly={isViewingOtherProfile}
                isAdmin={canApproveAbsence}
              />
            </Tabs.Panel>
          )}

          <Tabs.Panel value="kpi" className={displayClasses.tabContent}>
            <KPITab
              user={user}
              isAgentView={UserUtils.isAgentDashboardUser(loggedUser) && !isViewingOtherProfile}
            />
          </Tabs.Panel>

          {canViewDocumentation && (
            <Tabs.Panel value="documentation" pt="xl">
              <DocumentationTab user={user} />
            </Tabs.Panel>
          )}
        </Tabs>
      </Container>

      {canRequestAbsence && (
        <AbsenceRequestModal
          opened={modalOpened}
          onClose={handleModalClose}
          onSuccess={fetchAbsences}
          absenceId={selectedAbsenceId}
          holidays={holidays}
        />
      )}

      <ProfileUserDataModal
        opened={isEditUserDataOpened}
        onClose={handleEditUserDataClose}
        onSubmit={handleUserDataSubmit}
        loading={isUserDataSaving || isUserDataLoading}
        options={userDataOptions}
        optionsLoading={isUserDataOptionsLoading}
        userData={userData}
        user={user}
      />

      {/* Status Change Modal (Admin approve/reject) */}
      <Modal
        opened={statusModalOpened}
        onClose={handleStatusModalClose}
        title={t("profilePage.absences.statusModal.title")}
        centered
        size="sm"
        padding="lg"
        styles={{ title: { fontSize: 18, fontWeight: 600 } }}
      >
        <Text size="sm" mb="xl">
          {t("profilePage.absences.statusModal.description")}
        </Text>

        {selectedAbsenceForStatus?.note && (
          <Box mb="xl" ta="center">
            <Text size="sm" c="primary" fw={500}>
              {t("profilePage.absences.statusModal.agentNote")}
            </Text>
            <Text size="sm">{selectedAbsenceForStatus.note}</Text>
          </Box>
        )}

        <Group justify="center" gap="md">
          <Button
            color="red"
            radius="md"
            onClick={handleReject}
            loading={isRejectLoading}
            disabled={isStatusActionLoading}
          >
            {t("profilePage.absences.statusModal.reject")}
          </Button>
          <Button
            color="primary"
            radius="md"
            onClick={handleApprove}
            loading={isApproveLoading}
            disabled={isStatusActionLoading}
          >
            {t("profilePage.absences.statusModal.approve")}
          </Button>
        </Group>
      </Modal>
    </LayoutStandard>
  );
};

export default ProfilePage;
